import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  LinearProgress,
  Alert,
  Avatar,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Autocomplete,
  Checkbox,
} from "@mui/material";
import {
  Search as SearchIcon,
  Verified as VerifiedIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  School as SchoolIcon,
  PictureAsPdf as PdfIcon,
  Warning as WarningIcon,
  VpnKey as VpnKeyIcon,
  Receipt as ReceiptIcon,
  ContentCopy as CopyIcon,
  Autorenew as AutorenewIcon,
  ViewColumn as ViewColumnIcon,
} from "@mui/icons-material";
import api, {
  generateUserPDF,
  deleteUser,
  bulkDeleteUsers,
  changeUserPassword,
  getUserChallans,
  deleteChallan,
  bulkDeleteChallans,
  regenerateChallan,
  updateUser,
} from "../services/api";
import { AVAILABLE_COURSES } from "../constants/courses";

// Get the user API base URL
const USER_BASE_URL =
  process.env.REACT_APP_USER_API_URL ||
  "https://backend.hunarmandpunjab.org.pk";

interface User {
  _id: string;
  rollNumber: string;
  secondRollNumber: string;
  email: string;
  fullName: string;
  fatherName: string;
  cnic: string;
  mobile: string;
  dateOfBirth: string;
  gender: "male" | "female";
  qualification: string;
  courses: string[];
  secondEnrolledCourses: string[];
  permanentAddress: string;
  city: string;
  isVerified: boolean;
  createdAt: string;
  updatedAt: string;
  challans: any;
  referralCode?: string;
}

interface UserResponse {
  users: User[];
  pagination: {
    current: number;
    pages: number;
    total: number;
    limit: number;
  };
}

interface UserStats {
  total: number;
  verified: number;
  unverified: number;
  male: number;
  female: number;
  recent: number;
}

const AVAILABLE_EXPORT_COLUMNS = [
  { key: "username", label: "username (Full Name)" },
  { key: "duedate", label: "duedate (Custom Due Date)" },
  { key: "challanId", label: "challanId (Challan ID)" },
  { key: "paddedChallanId", label: "paddedChallanId (8-Digit Padded Challan ID)" },
  { key: "psid", label: "psid (Prefix + Challan ID)" },
  { key: "rollNumber", label: "Roll Number" },
  { key: "secondRollNumber", label: "2nd Enrollment Roll Number" },
  { key: "fullName", label: "Full Name" },
  { key: "fatherName", label: "Father Name" },
  { key: "email", label: "Email" },
  { key: "mobile", label: "Mobile" },
  { key: "cnic", label: "CNIC" },
  { key: "dateOfBirth", label: "Date of Birth" },
  { key: "gender", label: "Gender" },
  { key: "qualification", label: "Qualification" },
  { key: "city", label: "City" },
  { key: "permanentAddress", label: "Permanent Address" },
  { key: "courses", label: "Courses" },
  { key: "secondEnrolledCourses", label: "2nd Enrollment Courses" },
  { key: "challanPaid", label: "Challan Paid Status" },
  { key: "referralCode", label: "Referral Code" },
  { key: "verificationStatus", label: "Verification Status" },
  { key: "createdAt", label: "Created Date" },
  { key: "updatedAt", label: "Updated Date" },
];

function Users() {
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [gender, setGender] = useState("");
  const [qualification, setQualification] = useState("");
  const [city, setCity] = useState("");
  const [isVerified, setIsVerified] = useState("");
  const [challanPaid, setChallanPaid] = useState("");
  const [noChallan, setNoChallan] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [specificDate, setSpecificDate] = useState("");
  const [fromRollNumber, setFromRollNumber] = useState("");
  const [toRollNumber, setToRollNumber] = useState("");
  const [lastGeneratedUser, setLastGeneratedUser] = useState<{ id: string; fullName?: string; rollNumber?: string } | null>(() => {
    try {
      const saved = localStorage.getItem("lastGeneratedPdfUser");
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      return null;
    }
  });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Course editing states
  const [courseEditDialogOpen, setCourseEditDialogOpen] = useState(false);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState<User | null>(
    null
  );
  const [editedCourses, setEditedCourses] = useState<string[]>([]);
  const [editingCourseType, setEditingCourseType] = useState<
    "courses" | "secondEnrolledCourses"
  >("courses");
  const [newCourse, setNewCourse] = useState("");
  const [courseEditLoading, setCourseEditLoading] = useState(false);
  const [courseEditError, setCourseEditError] = useState("");

  // PDF generation and delete states
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUserForAction, setSelectedUserForAction] =
    useState<User | null>(null);
  const [adminApiKey, setAdminApiKey] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const [pdfGenerating, setPdfGenerating] = useState(false);
  
  // Change password states
  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [changePasswordReason, setChangePasswordReason] = useState("");
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);

  // Edit Name states
  const [editNameModalOpen, setEditNameModalOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [editNameReason, setEditNameReason] = useState("");
  const [nameChangeLoading, setNameChangeLoading] = useState(false);

  // User challans modal states
  const [challansModalOpen, setChallansModalOpen] = useState(false);
  const [selectedUserForChallans, setSelectedUserForChallans] = useState<User | null>(null);
  const [userChallans, setUserChallans] = useState<any[]>([]);
  const [loadingChallans, setLoadingChallans] = useState(false);
  const [challansError, setChallansError] = useState("");

  // Challan delete states
  const [deleteChallanConfirmOpen, setDeleteChallanConfirmOpen] = useState(false);
  const [selectedChallanToDelete, setSelectedChallanToDelete] = useState<any | null>(null);
  const [deleteChallanReason, setDeleteChallanReason] = useState("");
  const [deletingChallan, setDeletingChallan] = useState(false);

  // Bulk challan delete states
  const [selectedChallanIds, setSelectedChallanIds] = useState<string[]>([]);
  const [bulkDeleteChallanConfirmOpen, setBulkDeleteChallanConfirmOpen] = useState(false);
  const [bulkDeleteChallanReason, setBulkDeleteChallanReason] = useState("");

  // Download Column Selection modal states
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>(() =>
    AVAILABLE_EXPORT_COLUMNS.map((c) => c.key)
  );
  const [exportDueDate, setExportDueDate] = useState<string>(() => {
    // Default due date: 7 days from today in YYYY-MM-DD
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toISOString().split("T")[0];
  });
  const [exportPsidPrefix, setExportPsidPrefix] = useState<string>("267200309");

  // Challan regenerate states
  const [regenerateConfirmOpen, setRegenerateConfirmOpen] = useState(false);
  const [selectedChallanToRegenerate, setSelectedChallanToRegenerate] = useState<any | null>(null);
  const [regenerateReason, setRegenerateReason] = useState("");
  const [regenerating, setRegenerating] = useState(false);

  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [debouncedFromRollNumber, setDebouncedFromRollNumber] = useState(fromRollNumber);
  const [debouncedToRollNumber, setDebouncedToRollNumber] = useState(toRollNumber);
  const [debouncedCity, setDebouncedCity] = useState(city);
  const [debouncedQualification, setDebouncedQualification] = useState(qualification);

  // Debounce text inputs by 400ms
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFromRollNumber(fromRollNumber);
    }, 400);
    return () => clearTimeout(handler);
  }, [fromRollNumber]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedToRollNumber(toRollNumber);
    }, 400);
    return () => clearTimeout(handler);
  }, [toRollNumber]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedCity(city);
    }, 400);
    return () => clearTimeout(handler);
  }, [city]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQualification(qualification);
    }, 400);
    return () => clearTimeout(handler);
  }, [qualification]);

  useEffect(() => {
    fetchUsers();
  }, [
    page,
    rowsPerPage,
    debouncedSearch,
    gender,
    debouncedQualification,
    debouncedCity,
    isVerified,
    challanPaid,
    noChallan,
    startDate,
    endDate,
    specificDate,
    debouncedFromRollNumber,
    debouncedToRollNumber,
  ]);

  useEffect(() => {
    fetchUserStats();
  }, []);

  // Debug effect for editedCourses
  useEffect(() => {
    if (editedCourses.length > 0) {
      console.log("editedCourses state changed:", editedCourses);
    }
  }, [editedCourses]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: page + 1,
        limit: rowsPerPage,
      };

      if (debouncedSearch) params.search = debouncedSearch;
      if (gender) params.gender = gender;
      if (debouncedQualification) params.qualification = debouncedQualification;
      if (debouncedCity) params.city = debouncedCity;
      if (isVerified !== "") params.isVerified = isVerified;
      if (challanPaid !== "") params.challanPaid = challanPaid;
      if (noChallan !== "") params.noChallan = noChallan;
      if (specificDate) {
        params.startDate = specificDate;
        params.endDate = specificDate;
      } else {
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
      }
      if (debouncedFromRollNumber) params.fromRollNumber = debouncedFromRollNumber;
      if (debouncedToRollNumber) params.toRollNumber = debouncedToRollNumber;
      const response = await api.get("/users", { params });
      const data: UserResponse = response.data.data;
      setUsers(data.users);
      setTotal(data.pagination.total);
    } catch (err) {
      console.error("Error fetching users:", err);
      setError("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStats = async () => {
    try {
      const response = await api.get("/users/stats");
      setStats(response.data.data);
    } catch (err) {
      console.error("Error fetching user stats:", err);
    }
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const clearFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    setGender("");
    setQualification("");
    setDebouncedQualification("");
    setCity("");
    setDebouncedCity("");
    setIsVerified("");
    setChallanPaid("");
    setNoChallan("");
    setStartDate("");
    setEndDate("");
    setSpecificDate("");
    setFromRollNumber("");
    setDebouncedFromRollNumber("");
    setToRollNumber("");
    setDebouncedToRollNumber("");
    clearHighlightedUser();
    setPage(0);
  };

  const clearHighlightedUser = () => {
    setLastGeneratedUser(null);
    try {
      localStorage.removeItem("lastGeneratedPdfUser");
    } catch (e) {}
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-PK");
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const getFilterSummary = () => {
    const hasFilters =
      search ||
      gender ||
      qualification ||
      city ||
      isVerified !== "" ||
      challanPaid !== "" ||
      noChallan !== "" ||
      startDate ||
      endDate ||
      specificDate ||
      fromRollNumber ||
      toRollNumber ||
      !!lastGeneratedUser;
    const totalDbUsers = stats?.total || 0;

    if (!hasFilters) {
      return `Total: ${totalDbUsers.toLocaleString()} users in database`;
    }

    const filterParts = [];
    if (search) filterParts.push(`"${search}"`);
    if (gender) filterParts.push(`${gender}`);
    if (qualification) filterParts.push(`qualification: ${qualification}`);
    if (city) filterParts.push(`city: ${city}`);
    if (isVerified !== "")
      filterParts.push(isVerified === "true" ? "verified" : "unverified");
    if (challanPaid !== "")
      filterParts.push(challanPaid === "true" ? "paid" : "unpaid");
    if (noChallan !== "")
      filterParts.push(noChallan === "true" ? "no challan" : "has challan");
    if (startDate) filterParts.push(`from: ${startDate}`);
    if (endDate) filterParts.push(`to: ${endDate}`);
    if (specificDate) filterParts.push(`date: ${specificDate}`);
    if (fromRollNumber) filterParts.push(`from roll: ${fromRollNumber}`);
    if (toRollNumber) filterParts.push(`to roll: ${toRollNumber}`);
    if (lastGeneratedUser) filterParts.push(`highlighted: ${lastGeneratedUser.fullName || lastGeneratedUser.rollNumber || "user"}`);
    const filterText =
      filterParts.length > 0 ? ` (${filterParts.join(", ")})` : "";
    return `Showing ${total.toLocaleString()} of ${totalDbUsers.toLocaleString()} users${filterText}`;
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (search) count++;
    if (gender) count++;
    if (qualification) count++;
    if (city) count++;
    if (isVerified !== "") count++;
    if (challanPaid !== "") count++;
    if (noChallan !== "") count++;
    if (startDate) count++;
    if (endDate) count++;
    if (specificDate) count++;
    if (fromRollNumber) count++;
    if (toRollNumber) count++;
    if (lastGeneratedUser) count++;
    return count;
  };

  const handleOpenDownloadModal = () => {
    setDownloadModalOpen(true);
  };

  const handleToggleColumn = (colKey: string) => {
    setSelectedColumns((prev) =>
      prev.includes(colKey)
        ? prev.filter((k) => k !== colKey)
        : [...prev, colKey]
    );
  };

  const handleSelectAllColumns = () => {
    setSelectedColumns(AVAILABLE_EXPORT_COLUMNS.map((c) => c.key));
  };

  const handleDeselectAllColumns = () => {
    setSelectedColumns([]);
  };

  const downloadUsers = async () => {
    if (selectedColumns.length === 0) {
      alert("Please select at least one column to download.");
      return;
    }

    try {
      setLoading(true);
      setDownloadModalOpen(false);

      // Build the same params as the fetchUsers function
      const params: any = {};

      if (search) params.search = search;
      if (gender) params.gender = gender;
      if (qualification) params.qualification = qualification;
      if (city) params.city = city;
      if (isVerified !== "") params.isVerified = isVerified;
      if (challanPaid !== "") params.challanPaid = challanPaid;
      if (noChallan !== "") params.noChallan = noChallan;
      if (specificDate) {
        params.startDate = specificDate;
        params.endDate = specificDate;
      } else {
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
      }
      if (fromRollNumber) params.fromRollNumber = fromRollNumber;
      if (toRollNumber) params.toRollNumber = toRollNumber;
      params.columns = selectedColumns.join(",");
      if (exportDueDate) params.dueDate = exportDueDate;
      if (exportPsidPrefix) params.psidPrefix = exportPsidPrefix;

      // Call the download endpoint
      const response = await api.get("/users/download", {
        params,
        responseType: "blob", // Important for file downloads
      });

      // Create download link
      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Generate filename with current date
      const timestamp = new Date().toISOString().split("T")[0];
      const filterInfo = getActiveFilterCount() > 0 ? "_filtered" : "_all";
      link.download = `users_export${filterInfo}_${timestamp}.csv`;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading users:", err);
      setError("Failed to download users data");
    } finally {
      setLoading(false);
    }
  };

  // PDF generation handler
  const handleGeneratePDF = (user: User) => {
    setSelectedUserForAction(user);
    setPdfModalOpen(true);
  };

  const confirmGeneratePDF = async () => {
    if (!adminApiKey.trim()) {
      alert("Please provide the admin API key");
      return;
    }

    if (!selectedUserForAction) {
      alert("No user selected");
      return;
    }

    try {
      setPdfGenerating(true);

      const response = await generateUserPDF(
        selectedUserForAction._id,
        adminApiKey,
        {
          fullName: selectedUserForAction.fullName,
          rollNumber: selectedUserForAction.rollNumber,
          email: selectedUserForAction.email,
        }
      );
      const data = response.data;

      // Both existing and new challans now return JSON with fileUrl
      if (data.status === "success" && data.data && data.data.fileUrl) {
        // Save generated user info in state and localStorage for row highlighting and filter chip
        if (selectedUserForAction) {
          const highlightInfo = {
            id: selectedUserForAction._id,
            fullName: selectedUserForAction.fullName,
            rollNumber: selectedUserForAction.rollNumber,
          };
          setLastGeneratedUser(highlightInfo);
          try {
            localStorage.setItem("lastGeneratedPdfUser", JSON.stringify(highlightInfo));
          } catch (e) {}
        }

        // Extract path from fileUrl starting at /uploads
        const fileUrl = data.data.fileUrl;
        const uploadPath = fileUrl.substring(fileUrl.indexOf("/uploads"));
        window.open(`${USER_BASE_URL}${uploadPath}`, "_blank");

        // Show appropriate success message
        if (data.message === "Existing challan found") {
          alert(`Existing challan opened: ${data.data.challanNumber}`);
        } else if (data.message === "Challan generated successfully") {
          alert(`New challan generated and opened: ${data.data.challanNumber}`);
        } else {
          alert(`Challan opened: ${data.data.challanNumber}`);
        }
      } else {
        throw new Error("Invalid response format from challan API");
      }

      setPdfModalOpen(false);
      setAdminApiKey("");
      
      // Refetch users API with active filters and stats
      fetchUsers();
      fetchUserStats();

      // If the challan modal is open for this user, refresh their challan list
      if (challansModalOpen && selectedUserForChallans && selectedUserForAction && selectedUserForChallans._id === selectedUserForAction._id) {
        handleManageChallans(selectedUserForAction);
      }
    } catch (error: any) {
      console.error("Error generating challan:", error);
      alert(
        `Failed to generate challan: ${
          error.response?.data?.message || error.message
        }`
      );
    } finally {
      setPdfGenerating(false);
    }
  };

  // Change password handler
  const handleChangePassword = (user: User) => {
    setSelectedUserForAction(user);
    setChangePasswordModalOpen(true);
  };

  const confirmChangePassword = async () => {
    if (!newPassword.trim() || newPassword.length < 8) {
      alert("Password must be at least 8 characters long");
      return;
    }

    if (!changePasswordReason.trim()) {
      alert("Please provide a reason for changing the password");
      return;
    }

    if (!selectedUserForAction) {
      alert("No user selected");
      return;
    }

    try {
      setPasswordChangeLoading(true);
      await changeUserPassword(selectedUserForAction._id, newPassword, changePasswordReason);
      
      // Clear state and close modal
      setSelectedUserForAction(null);
      setChangePasswordModalOpen(false);
      setNewPassword("");
      setChangePasswordReason("");

      alert("User password updated successfully");
    } catch (error: any) {
      console.error("Error changing password:", error);
      alert(
        `Failed to change password: ${
          error.response?.data?.message || error.message
        }`
      );
    } finally {
      setPasswordChangeLoading(false);
    }
  };

  // Edit Name handlers
  const handleEditName = (user: User) => {
    setSelectedUserForAction(user);
    setNewName(user.fullName || "");
    setEditNameReason("");
    setEditNameModalOpen(true);
  };

  const confirmEditName = async () => {
    if (!newName.trim()) {
      alert("Name cannot be empty");
      return;
    }

    if (!editNameReason.trim()) {
      alert("Please provide a reason for editing the name");
      return;
    }

    if (!selectedUserForAction) {
      alert("No user selected");
      return;
    }

    try {
      setNameChangeLoading(true);
      await updateUser(selectedUserForAction._id, {
        fullName: newName.trim(),
        reason: editNameReason.trim(),
      });

      // Update local state
      setUsers((prevUsers) =>
        prevUsers.map((u) =>
          u._id === selectedUserForAction._id ? { ...u, fullName: newName.trim() } : u
        )
      );

      // Clear state and close modal
      setSelectedUserForAction(null);
      setEditNameModalOpen(false);
      setNewName("");
      setEditNameReason("");

      alert("User name updated successfully");
    } catch (error: any) {
      console.error("Error editing name:", error);
      alert(
        `Failed to edit name: ${
          error.response?.data?.message || error.message
        }`
      );
    } finally {
      setNameChangeLoading(false);
    }
  };

  // User deletion handler
  const handleDeleteUser = (user: User) => {
    setSelectedUserForAction(user);
    setDeleteModalOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!deleteReason.trim()) {
      alert("Please provide a reason for deletion");
      return;
    }

    if (!selectedUserForAction) {
      alert("No user selected");
      return;
    }

    try {
      setLoading(true);

      await deleteUser(selectedUserForAction._id);

      // Refresh the users list and stats
      await Promise.all([fetchUsers(), fetchUserStats()]);

      // Clear state and close modal
      setSelectedUserForAction(null);
      setDeleteModalOpen(false);
      setDeleteReason("");
      setSelectedIds(prev => prev.filter(id => id !== selectedUserForAction._id));

      alert("User and all related data deleted successfully");
    } catch (error) {
      console.error("Error deleting user:", error);
      alert("Failed to delete user");
    } finally {
      setLoading(false);
    }
  };

  const handleManageChallans = async (user: User) => {
    setSelectedUserForChallans(user);
    setChallansModalOpen(true);
    setLoadingChallans(true);
    setChallansError("");
    setUserChallans([]);
    setSelectedChallanIds([]);
    try {
      const response = await getUserChallans(user._id);
      if (response.success && response.data) {
        setUserChallans(response.data.challans || []);
      } else {
        setChallansError("Failed to retrieve challans data");
      }
    } catch (err: any) {
      console.error("Error fetching user challans:", err);
      setChallansError(err.response?.data?.message || "Failed to load challans");
    } finally {
      setLoadingChallans(false);
    }
  };

  const handleChallanSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelectedIds = userChallans.map((c) => c._id);
      setSelectedChallanIds(newSelectedIds);
    } else {
      setSelectedChallanIds([]);
    }
  };

  const handleChallanSelectOne = (id: string) => {
    const selectedIndex = selectedChallanIds.indexOf(id);
    let newSelectedIds: string[] = [];

    if (selectedIndex === -1) {
      newSelectedIds = [...selectedChallanIds, id];
    } else if (selectedIndex === 0) {
      newSelectedIds = [...selectedChallanIds.slice(1)];
    } else if (selectedIndex === selectedChallanIds.length - 1) {
      newSelectedIds = [...selectedChallanIds.slice(0, -1)];
    } else if (selectedIndex > 0) {
      newSelectedIds = [
        ...selectedChallanIds.slice(0, selectedIndex),
        ...selectedChallanIds.slice(selectedIndex + 1),
      ];
    }
    setSelectedChallanIds(newSelectedIds);
  };

  const handleOpenBulkDeleteChallans = () => {
    setBulkDeleteChallanReason("");
    setBulkDeleteChallanConfirmOpen(true);
  };

  const handleBulkDeleteChallansConfirm = async () => {
    if (selectedChallanIds.length === 0) return;
    if (!bulkDeleteChallanReason.trim()) {
      alert("Please provide a reason for bulk deleting the challans");
      return;
    }

    setDeletingChallan(true);
    try {
      const response = await bulkDeleteChallans(selectedChallanIds, bulkDeleteChallanReason);
      if (response.success) {
        alert(`${selectedChallanIds.length} challans and associated files deleted successfully`);
        // Remove deleted ones from modal view
        setUserChallans((prev) => prev.filter((c) => !selectedChallanIds.includes(c._id)));
        setSelectedChallanIds([]);
        setBulkDeleteChallanConfirmOpen(false);
        // Refresh users list
        fetchUsers();
      } else {
        alert(response.message || "Failed to bulk delete challans");
      }
    } catch (err: any) {
      console.error("Error bulk deleting challans:", err);
      alert(err.response?.data?.message || "Failed to bulk delete challans");
    } finally {
      setDeletingChallan(false);
    }
  };

  const handleOpenRegenerateChallan = (challan: any) => {
    setSelectedChallanToRegenerate(challan);
    setRegenerateReason("");
    setRegenerateConfirmOpen(true);
  };

  const handleRegenerateChallanConfirm = async () => {
    if (!selectedChallanToRegenerate) return;
    if (!regenerateReason.trim()) {
      alert("Please provide a reason for regenerating the challan PDF");
      return;
    }

    setRegenerating(true);
    try {
      const response = await regenerateChallan(selectedChallanToRegenerate._id, regenerateReason);
      if (response.success && response.data) {
        alert("Challan PDF file regenerated successfully");
        
        // Open the regenerated PDF in a new tab
        const fileUrl = response.data.fileUrl;
        const uploadPath = fileUrl.substring(fileUrl.indexOf("/uploads"));
        window.open(`${USER_BASE_URL}${uploadPath}`, "_blank");

        setRegenerateConfirmOpen(false);
        setSelectedChallanToRegenerate(null);
        
        // Refresh the list inside the modal
        if (selectedUserForChallans) {
          handleManageChallans(selectedUserForChallans);
        }
      } else {
        alert(response.message || "Failed to regenerate challan");
      }
    } catch (err: any) {
      console.error("Error regenerating challan:", err);
      alert(err.response?.data?.message || "Failed to regenerate challan");
    } finally {
      setRegenerating(false);
    }
  };

  const handleCopyChallanLink = (challan: any) => {
    if (!challan.path) {
      alert("No PDF path associated with this challan");
      return;
    }
    const uploadIndex = challan.path.indexOf("/uploads");
    const uploadPath = uploadIndex !== -1 ? challan.path.substring(uploadIndex) : challan.path;
    const fullLink = `${USER_BASE_URL}${uploadPath}`;
    navigator.clipboard.writeText(fullLink)
      .then(() => {
        alert(`Challan link copied to clipboard:\n${fullLink}`);
      })
      .catch((err) => {
        console.error("Failed to copy link:", err);
        alert("Failed to copy link to clipboard");
      });
  };

  const handleOpenDeleteChallan = (challan: any) => {
    setSelectedChallanToDelete(challan);
    setDeleteChallanReason("");
    setDeleteChallanConfirmOpen(true);
  };

  const handleDeleteChallanConfirm = async () => {
    if (!selectedChallanToDelete) return;
    if (!deleteChallanReason.trim()) {
      alert("Please provide a reason for deleting the challan");
      return;
    }

    setDeletingChallan(true);
    try {
      const response = await deleteChallan(selectedChallanToDelete._id, deleteChallanReason);
      if (response.success) {
        alert("Challan and associated files deleted successfully");
        // Remove from list in UI
        setUserChallans((prev) => prev.filter((c) => c._id !== selectedChallanToDelete._id));
        setDeleteChallanConfirmOpen(false);
        setSelectedChallanToDelete(null);
        // Refresh users list to update challan counts/status
        fetchUsers();
      } else {
        alert(response.message || "Failed to delete challan");
      }
    } catch (err: any) {
      console.error("Error deleting challan:", err);
      alert(err.response?.data?.message || "Failed to delete challan");
    } finally {
      setDeletingChallan(false);
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelectedIds = users.map((user) => user._id);
      setSelectedIds(newSelectedIds);
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: string) => {
    const selectedIndex = selectedIds.indexOf(id);
    let newSelectedIds: string[] = [];

    if (selectedIndex === -1) {
      newSelectedIds = [...selectedIds, id];
    } else {
      newSelectedIds = selectedIds.filter((item) => item !== id);
    }

    setSelectedIds(newSelectedIds);
  };

  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);

  const handleBulkDelete = () => {
    setBulkDeleteModalOpen(true);
  };

  const confirmBulkDelete = async () => {
    if (!deleteReason.trim()) {
      alert("Please provide a reason for deletion");
      return;
    }

    try {
      setLoading(true);
      await bulkDeleteUsers(selectedIds);

      // Refresh the users list and stats
      await Promise.all([fetchUsers(), fetchUserStats()]);

      setBulkDeleteModalOpen(false);
      setSelectedIds([]);
      setDeleteReason("");

      alert(`${selectedIds.length} users and all related data deleted successfully`);
    } catch (error) {
      console.error("Error bulk deleting users:", error);
      alert("Failed to delete selected users");
    } finally {
      setLoading(false);
    }
  };

  // Use stats data instead of current page data
  const verifiedCount = stats?.verified || 0;
  const unverifiedCount = stats?.unverified || 0;

  const handleEditCourses = (
    user: User,
    type: "courses" | "secondEnrolledCourses" = "courses"
  ) => {
    console.log(
      `Opening ${type} edit for user:`,
      user.fullName,
      "Current courses:",
      user[type]
    );

    // Ensure courses is always an array
    const userCourses = Array.isArray(user[type]) ? user[type] : [];
    console.log("Normalized user courses:", userCourses);

    setSelectedUserForEdit(user);
    setEditingCourseType(type);
    setEditedCourses([...userCourses]); // Make sure we copy the array properly
    setNewCourse("");
    setCourseEditError("");
    setCourseEditDialogOpen(true);
  };

  const handleAddCourse = () => {
    console.log("Attempting to add course:", newCourse.trim());
    console.log("Current editedCourses:", editedCourses);
    console.log("Available courses:", AVAILABLE_COURSES);

    if (
      newCourse.trim() &&
      !editedCourses.includes(newCourse.trim()) &&
      AVAILABLE_COURSES.includes(newCourse.trim())
    ) {
      const updatedCourses = [...editedCourses, newCourse.trim()];
      console.log(
        "Adding course:",
        newCourse.trim(),
        "Updated courses:",
        updatedCourses
      );
      setEditedCourses(updatedCourses);
      setNewCourse("");
    } else {
      console.log("Course add validation failed:", {
        hasValue: !!newCourse.trim(),
        alreadyExists: editedCourses.includes(newCourse.trim()),
        isAvailable: AVAILABLE_COURSES.includes(newCourse.trim()),
      });
    }
  };

  const handleRemoveCourse = (courseToRemove: string) => {
    console.log(
      "Removing course:",
      courseToRemove,
      "Current editedCourses:",
      editedCourses
    );
    const updatedCourses = editedCourses.filter(
      (course) => course !== courseToRemove
    );
    console.log("Updated courses after removal:", updatedCourses);
    setEditedCourses(updatedCourses);
  };

  const handleSaveCourses = async () => {
    if (!selectedUserForEdit) return;

    try {
      setCourseEditLoading(true);
      setCourseEditError("");

      console.log("Saving courses for user:", selectedUserForEdit._id);
      console.log("Type:", editingCourseType);
      console.log("Original courses:", selectedUserForEdit[editingCourseType]);
      console.log("New courses:", editedCourses);

      const payload = {
        [editingCourseType]: editedCourses,
        reason: "Admin updated user courses",
      };

      console.log("API payload:", payload);

      const response = await api.put(
        `/users/${selectedUserForEdit._id}`,
        payload
      );
      console.log("API response:", response.data);

      // Update the user in the local state
      setUsers(
        users.map((user) =>
          user._id === selectedUserForEdit._id
            ? { ...user, [editingCourseType]: editedCourses }
            : user
        )
      );

      setCourseEditDialogOpen(false);
      setSelectedUserForEdit(null);
      setEditedCourses([]);
    } catch (err) {
      console.error("Error updating user courses:", err);
      setCourseEditError("Failed to update courses. Please try again.");
    } finally {
      setCourseEditLoading(false);
    }
  };

  const handleCloseCourseDialog = () => {
    setCourseEditDialogOpen(false);
    setSelectedUserForEdit(null);
    setEditedCourses([]);
    setNewCourse("");
    setCourseEditError("");
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }
  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Users Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {getFilterSummary()}
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchUsers} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Summary Cards */}
      <Box display="flex" flexWrap="wrap" gap={2} mb={3}>
        <Card sx={{ minWidth: 200 }}>
          <CardContent>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Verified Users
                </Typography>
                <Typography variant="h5" color="success.main">
                  {verifiedCount}
                </Typography>
              </Box>
              <VerifiedIcon color="success" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 200 }}>
          <CardContent>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Unverified Users
                </Typography>
                <Typography variant="h5" color="warning.main">
                  {unverifiedCount}
                </Typography>
              </Box>
              <CancelIcon color="warning" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 200 }}>
          <CardContent>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Available Courses
                </Typography>
                <Typography variant="h5" color="info.main">
                  {AVAILABLE_COURSES.length}
                </Typography>
              </Box>
              <SchoolIcon color="info" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Box display="flex" alignItems="center">
            <FilterIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Filters</Typography>
            {getActiveFilterCount() > 0 && (
              <Chip
                label={`${getActiveFilterCount()} active`}
                size="small"
                color="primary"
                sx={{ ml: 1 }}
              />
            )}
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <PersonIcon color="primary" />
            <Typography variant="h6" color="primary" fontWeight="bold">
              {total.toLocaleString()} Users Found
            </Typography>
          </Box>
        </Box>

        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <TextField
            label="Search"
            placeholder="Name, email, roll number, CNIC, mobile"
            value={search}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
              ),
            }}
            sx={{ minWidth: 300 }}
          />

          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Gender</InputLabel>
            <Select
              value={gender}
              label="Gender"
              onChange={(e) => setGender(e.target.value as string)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="male">Male</MenuItem>
              <MenuItem value="female">Female</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Challan Paid</InputLabel>
            <Select
              value={challanPaid}
              label="Challan Paid"
              onChange={(e) => setChallanPaid(e.target.value as string)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="false">Unpaid</MenuItem>
              <MenuItem value="true">Paid</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Challan Status</InputLabel>
            <Select
              value={noChallan}
              label="Challan Status"
              onChange={(e) => setNoChallan(e.target.value as string)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">No Challan</MenuItem>
              <MenuItem value="false">Has Challan</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="City"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="Filter by city"
            sx={{ minWidth: 150 }}
          />

          <TextField
            label="Qualification"
            value={qualification}
            onChange={(e) => setQualification(e.target.value)}
            placeholder="e.g. bachelor"
            sx={{ minWidth: 150 }}
          />

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Verification</InputLabel>
            <Select
              value={isVerified}
              label="Verification"
              onChange={(e) => setIsVerified(e.target.value as string)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Verified</MenuItem>
              <MenuItem value="false">Unverified</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="From Roll Number"
            value={fromRollNumber}
            onChange={(e) => {
              setFromRollNumber(e.target.value);
              setPage(0);
            }}
            placeholder="e.g. 5000 or HM-2025-5000"
            sx={{ minWidth: 170 }}
          />

          <TextField
            label="To Roll Number"
            value={toRollNumber}
            onChange={(e) => {
              setToRollNumber(e.target.value);
              setPage(0);
            }}
            placeholder="e.g. 5050 or HM-2025-5050"
            sx={{ minWidth: 170 }}
          />

          <TextField
            label="Filter by Date"
            type="date"
            value={specificDate}
            onChange={(e) => {
              setSpecificDate(e.target.value);
              setStartDate("");
              setEndDate("");
              setPage(0);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />

          <TextField
            label="From Date"
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setSpecificDate("");
              setPage(0);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
            disabled={!!specificDate}
          />

          <TextField
            label="To Date"
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setSpecificDate("");
              setPage(0);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
            disabled={!!specificDate}
          />

          {lastGeneratedUser && (
            <Chip
              label={`Highlighted: ${lastGeneratedUser.fullName || lastGeneratedUser.rollNumber || "1 User"}`}
              color="success"
              variant="filled"
              onDelete={clearHighlightedUser}
              sx={{ fontWeight: "bold" }}
            />
          )}

          <IconButton onClick={clearFilters} title="Clear Filters">
            <CancelIcon />
          </IconButton>

          <Tooltip title="Download filtered users as CSV (Choose Columns)">
            <IconButton
              onClick={handleOpenDownloadModal}
              disabled={loading}
              color="primary"
              sx={{ ml: 1 }}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>

          {selectedIds.length > 0 && (
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleBulkDelete}
              disabled={loading}
              sx={{ ml: 2 }}
            >
              Delete Selected ({selectedIds.length})
            </Button>
          )}
        </Box>
      </Paper>

      {/* Users Table */}
      <Paper sx={{ width: "100%", overflow: "hidden", position: "relative" }}>
        {loading && <LinearProgress sx={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 2 }} />}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedIds.length > 0 && selectedIds.length < users.length
                    }
                    checked={users.length > 0 && selectedIds.length === users.length}
                    onChange={handleSelectAll}
                    disabled={loading}
                  />
                </TableCell>
                <TableCell>User</TableCell>
                <TableCell>Roll Number</TableCell>
                <TableCell>2nd Enrollment Roll Number</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Referral Code</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Gender</TableCell>
                <TableCell>Qualification</TableCell>
                <TableCell>Courses</TableCell>
                <TableCell>2nd Enrollment Courses</TableCell>
                <TableCell>Challan</TableCell>
                <TableCell>Challan Paid</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Registered</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ opacity: loading ? 0.6 : 1, transition: "opacity 0.2s" }}>
              {loading && users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    <Box sx={{ py: 4 }}>
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => {
                  const isGenerated = lastGeneratedUser?.id === user._id;
                  return (
                    <TableRow
                      key={user._id}
                      hover
                      selected={selectedIds.indexOf(user._id) !== -1}
                      sx={{
                        backgroundColor: isGenerated
                          ? "rgba(76, 175, 80, 0.14) !important"
                          : undefined,
                        "&:hover": {
                          backgroundColor: isGenerated
                            ? "rgba(76, 175, 80, 0.22) !important"
                            : undefined,
                        },
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIds.indexOf(user._id) !== -1}
                          onChange={() => handleSelectOne(user._id)}
                          disabled={loading}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Avatar sx={{ mr: 2, bgcolor: isGenerated ? "success.main" : "primary.main" }}>
                            {user.fullName ? (
                              getInitials(user.fullName)
                            ) : (
                              <PersonIcon />
                            )}
                          </Avatar>
                          <Box>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="subtitle2">
                                {user.fullName}
                              </Typography>
                              {isGenerated && (
                                <Chip
                                  label="PDF Generated"
                                  size="small"
                                  color="success"
                                  sx={{ fontSize: "0.65rem", height: 18 }}
                                />
                              )}
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {user.email}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Father: {user.fatherName}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {user.rollNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {user.secondRollNumber || "N/A"}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">{user.mobile}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        CNIC: {user.cnic}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {user?.referralCode || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{user.city}</Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.75rem" }}
                      >
                        {user.permanentAddress?.substring(0, 30)}...
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ textTransform: "capitalize" }}
                      >
                        {user.gender}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ textTransform: "capitalize" }}
                      >
                        {user.qualification}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Box>
                        {Array.isArray(user.courses) &&
                          user.courses
                            .slice(0, 2)
                            .map((course, index) => (
                              <Chip
                                key={index}
                                label={course}
                                size="small"
                                sx={{ mr: 0.5, mb: 0.5, fontSize: "0.7rem" }}
                              />
                            ))}
                        {Array.isArray(user.courses) &&
                          user.courses.length > 2 && (
                            <Chip
                              label={`+${user.courses.length - 2} more`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "0.7rem" }}
                            />
                          )}
                        <Tooltip title="Edit Courses">
                          <IconButton
                            size="small"
                            onClick={() => handleEditCourses(user, "courses")}
                            sx={{ ml: 1 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box>
                        {Array.isArray(user.secondEnrolledCourses) &&
                          user.secondEnrolledCourses
                            .slice(0, 2)
                            .map((course, index) => (
                              <Chip
                                key={index}
                                label={course}
                                size="small"
                                sx={{ mr: 0.5, mb: 0.5, fontSize: "0.7rem" }}
                              />
                            ))}
                        {Array.isArray(user.secondEnrolledCourses) &&
                          user.secondEnrolledCourses.length > 2 && (
                            <Chip
                              label={`+${
                                user.secondEnrolledCourses.length - 2
                              } more`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "0.7rem" }}
                            />
                          )}
                        <Tooltip title="Edit Second Enrollment Courses">
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleEditCourses(user, "secondEnrolledCourses")
                            }
                            sx={{ ml: 1 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {user.challans
                          .map((challan: any) => challan.id)
                          .join(", ") || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {user.challans
                          .map((challan: any) =>
                            challan.paid ? "Paid" : "Unpaid"
                          )
                          .join(", ") || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={
                          user.isVerified ? <VerifiedIcon /> : <CancelIcon />
                        }
                        label={user.isVerified ? "Verified" : "Unverified"}
                        color={user.isVerified ? "success" : "default"}
                        size="small"
                      />
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(user.createdAt)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Generate Challan PDF">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleGeneratePDF(user)}
                          >
                            <PdfIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Name">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleEditName(user)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Change Password">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleChangePassword(user)}
                          >
                            <VpnKeyIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Manage Challans">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleManageChallans(user)}
                          >
                            <ReceiptIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete User">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteUser(user)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Paper>

      {/* Bulk Delete Confirmation Modal */}
      <Dialog
        open={bulkDeleteModalOpen}
        onClose={() => !loading && setBulkDeleteModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningIcon color="error" />
          Bulk Delete Users
        </DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to delete <strong>{selectedIds.length}</strong> selected users?
            </Typography>
            <Typography variant="body2" color="error" sx={{ mt: 1, mb: 2 }}>
              This action is permanent and will also delete all associated challans, admit cards, and scholarship applications for these users.
            </Typography>
            <TextField
              fullWidth
              label="Reason for deletion"
              placeholder="e.g., Duplicate accounts, test data cleanup"
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              multiline
              rows={3}
              required
              disabled={loading}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setBulkDeleteModalOpen(false)} 
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmBulkDelete}
            color="error"
            variant="contained"
            disabled={!deleteReason.trim() || loading}
            startIcon={loading ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {loading ? "Deleting..." : `Delete ${selectedIds.length} Users`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Course Edit Dialog */}
      <Dialog
        open={courseEditDialogOpen}
        onClose={handleCloseCourseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <EditIcon sx={{ mr: 1 }} />
            Edit{" "}
            {editingCourseType === "courses"
              ? "Courses"
              : "Second Enrollment Courses"}{" "}
            for {selectedUserForEdit?.fullName}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {courseEditError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {courseEditError}
              </Alert>
            )}

            {/* Debug info */}
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Debug Info:</strong> Current editedCourses:{" "}
              {JSON.stringify(editedCourses)}
            </Alert>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Current Courses ({editedCourses.length})
              </Typography>
              {editedCourses.some(
                (course) => !AVAILABLE_COURSES.includes(course)
              ) && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Some courses are not in the current available courses list.
                  These will be preserved but cannot be re-added if removed.
                </Alert>
              )}
              <Box display="flex" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                {editedCourses.map((course, index) => (
                  <Chip
                    key={index}
                    label={course}
                    onDelete={() => handleRemoveCourse(course)}
                    deleteIcon={<DeleteIcon />}
                    color={
                      AVAILABLE_COURSES.includes(course) ? "primary" : "default"
                    }
                    variant="outlined"
                  />
                ))}
                {editedCourses.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No courses assigned
                  </Typography>
                )}
              </Box>
            </Box>

            <Box>
              <Autocomplete
                fullWidth
                value={null}
                onChange={(event, newValue) => {
                  if (newValue && !editedCourses.includes(newValue)) {
                    setEditedCourses([...editedCourses, newValue]);
                    setNewCourse("");
                  }
                }}
                inputValue={newCourse}
                onInputChange={(event, newInputValue) => {
                  setNewCourse(newInputValue);
                }}
                options={AVAILABLE_COURSES.filter(
                  (course) => !editedCourses.includes(course)
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Course to Add"
                    placeholder="Choose a course"
                    helperText="Selecting a course adds it immediately to the list above"
                  />
                )}
                noOptionsText="No more courses available"
              />
            </Box>

            <Typography variant="body2" color="text.secondary">
              <strong>Note:</strong> Changes will be logged for audit purposes.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Available Courses:</strong> {AVAILABLE_COURSES.length}{" "}
              predefined courses available for selection.
            </Typography>

            {/* Debug button */}
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const testCourse = AVAILABLE_COURSES[0];
                if (testCourse && !editedCourses.includes(testCourse)) {
                  setEditedCourses([...editedCourses, testCourse]);
                }
              }}
            >
              Test Add Course
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCourseDialog}>Cancel</Button>
          <Button
            onClick={handleSaveCourses}
            variant="contained"
            disabled={courseEditLoading}
            startIcon={
              courseEditLoading ? <CircularProgress size={16} /> : <EditIcon />
            }
          >
            {courseEditLoading ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* PDF Generation Modal */}
      <Dialog
        open={pdfModalOpen}
        onClose={() => setPdfModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <PdfIcon color="info" sx={{ mr: 1 }} />
            Generate Challan PDF
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Challan Generation:</strong> This will generate or open the
            challan PDF for the selected user in a new tab. If a challan already
            exists, it will be opened. If not, a new one will be created and
            opened.
          </Alert>
          {selectedUserForAction && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <strong>User:</strong> {selectedUserForAction.fullName}
              <br />
              <strong>Roll Number:</strong> {selectedUserForAction.rollNumber}
              <br />
              <strong>Email:</strong> {selectedUserForAction.email}
            </Alert>
          )}
          <TextField
            label="Admin API Key (Required)"
            placeholder="Enter your admin API key..."
            value={adminApiKey}
            onChange={(e) => setAdminApiKey(e.target.value)}
            fullWidth
            type="password"
            required
            helperText="This API key is required to authenticate the PDF generation request"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPdfModalOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmGeneratePDF}
            variant="contained"
            color="info"
            disabled={!adminApiKey.trim() || pdfGenerating}
            startIcon={
              pdfGenerating ? <CircularProgress size={20} /> : <PdfIcon />
            }
          >
            {pdfGenerating
              ? "Processing Challan..."
              : "Generate & Open Challan"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <DeleteIcon color="error" sx={{ mr: 1 }} />
            Confirm User Deletion
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>DANGER:</strong> This will permanently delete the user and
            ALL related data including:
            <ul>
              <li>User account information</li>
              <li>All associated challans</li>
              <li>All associated scholarships</li>
            </ul>
            This action cannot be undone!
          </Alert>
          {selectedUserForAction && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>User to be deleted:</strong>
              <br />
              Name: {selectedUserForAction.fullName}
              <br />
              Email: {selectedUserForAction.email}
              <br />
              Roll Number: {selectedUserForAction.rollNumber}
            </Alert>
          )}
          <TextField
            label="Reason for Deletion (Required)"
            placeholder="Explain why this user is being deleted..."
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            fullWidth
            multiline
            rows={3}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmDeleteUser}
            variant="contained"
            color="error"
            disabled={!deleteReason.trim() || loading}
            startIcon={<DeleteIcon />}
          >
            Delete User & All Data
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Password Modal */}
      <Dialog
        open={changePasswordModalOpen}
        onClose={() => !passwordChangeLoading && setChangePasswordModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <VpnKeyIcon color="warning" sx={{ mr: 1 }} />
            Change User Password
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedUserForAction && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>User:</strong> {selectedUserForAction.fullName} ({selectedUserForAction.email})
            </Alert>
          )}
          <TextField
            margin="dense"
            label="New Password"
            placeholder="Enter at least 8 characters..."
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            fullWidth
            type="password"
            required
            sx={{ mb: 2 }}
          />
          <TextField
            label="Reason for Change (Logged)"
            placeholder="Explain why the password is being changed..."
            value={changePasswordReason}
            onChange={(e) => setChangePasswordReason(e.target.value)}
            fullWidth
            multiline
            rows={3}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangePasswordModalOpen(false)} disabled={passwordChangeLoading}>Cancel</Button>
          <Button
            onClick={confirmChangePassword}
            variant="contained"
            color="warning"
            disabled={!newPassword.trim() || newPassword.length < 8 || !changePasswordReason.trim() || passwordChangeLoading}
            startIcon={passwordChangeLoading ? <CircularProgress size={20} /> : <VpnKeyIcon />}
          >
            {passwordChangeLoading ? "Changing Password..." : "Change Password"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Name Modal */}
      <Dialog
        open={editNameModalOpen}
        onClose={() => !nameChangeLoading && setEditNameModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <EditIcon color="info" sx={{ mr: 1 }} />
            Edit User Name
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedUserForAction && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Current Name:</strong> {selectedUserForAction.fullName} ({selectedUserForAction.email})
            </Alert>
          )}
          <TextField
            margin="dense"
            label="New Name"
            placeholder="Enter new full name..."
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            fullWidth
            required
            sx={{ mb: 2 }}
          />
          <TextField
            label="Reason for Change (Logged)"
            placeholder="Explain why the name is being changed..."
            value={editNameReason}
            onChange={(e) => setEditNameReason(e.target.value)}
            fullWidth
            multiline
            rows={3}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditNameModalOpen(false)} disabled={nameChangeLoading}>Cancel</Button>
          <Button
            onClick={confirmEditName}
            variant="contained"
            color="info"
            disabled={!newName.trim() || !editNameReason.trim() || nameChangeLoading}
            startIcon={nameChangeLoading ? <CircularProgress size={20} /> : <EditIcon />}
          >
            {nameChangeLoading ? "Updating..." : "Update Name"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Challans Modal */}
      <Dialog
        open={challansModalOpen}
        onClose={() => !deletingChallan && setChallansModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ borderBottom: "1px solid #e0e0e0", pb: 1, display: "flex", alignItems: "center", gap: 1 }}>
          <ReceiptIcon color="success" />
          <Typography variant="h6" component="span" fontWeight="bold">
            Manage User Challans
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedUserForChallans && (
            <Box mb={2} p={2} display="flex" flexWrap="wrap" gap={3} sx={{ backgroundColor: "#f5f5f5", borderRadius: 1 }}>
              <Box flex="1 1 200px">
                <Typography variant="caption" color="textSecondary" display="block">User Name</Typography>
                <Typography variant="body2" fontWeight="500">{selectedUserForChallans.fullName}</Typography>
              </Box>
              <Box flex="1 1 200px">
                <Typography variant="caption" color="textSecondary" display="block">Email Address</Typography>
                <Typography variant="body2" fontWeight="500">{selectedUserForChallans.email}</Typography>
              </Box>
              <Box flex="1 1 200px">
                <Typography variant="caption" color="textSecondary" display="block">Roll Number</Typography>
                <Typography variant="body2" fontWeight="500">{selectedUserForChallans.rollNumber || "N/A"}</Typography>
              </Box>
            </Box>
          )}

          {loadingChallans ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress size={40} />
            </Box>
          ) : challansError ? (
            <Alert severity="error" sx={{ my: 2 }}>
              {challansError}
            </Alert>
          ) : userChallans.length === 0 ? (
            <Box py={5} textAlign="center" display="flex" flexDirection="column" alignItems="center" gap={2}>
              <Typography variant="body1" color="textSecondary">
                No challans found for this user.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<PdfIcon />}
                onClick={() => selectedUserForChallans && handleGeneratePDF(selectedUserForChallans)}
              >
                Generate Challan
              </Button>
            </Box>
          ) : (
            <>
              {selectedChallanIds.length > 0 && (
                <Box mb={2} display="flex" justifyContent="space-between" alignItems="center" p={1.5} sx={{ backgroundColor: "#ffebee", borderRadius: 1 }}>
                  <Typography color="error" variant="subtitle2" fontWeight="600">
                    {selectedChallanIds.length} challans selected
                  </Typography>
                  <Button
                    color="error"
                    variant="contained"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={handleOpenBulkDeleteChallans}
                  >
                    Delete Selected
                  </Button>
                </Box>
              )}
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                <Table size="small">
                  <TableHead sx={{ backgroundColor: "#fafafa" }}>
                    <TableRow>
                       <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={
                            selectedChallanIds.length > 0 &&
                            selectedChallanIds.length < userChallans.length
                          }
                          checked={
                            userChallans.length > 0 &&
                            selectedChallanIds.length === userChallans.length
                          }
                          onChange={handleChallanSelectAll}
                          disabled={userChallans.length === 0}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Challan ID</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Date Generated</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userChallans.map((challan) => {
                      const isItemSelected = selectedChallanIds.indexOf(challan._id) !== -1;
                      return (
                        <TableRow 
                          key={challan._id} 
                          hover
                          selected={isItemSelected}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isItemSelected}
                              onChange={() => handleChallanSelectOne(challan._id)}
                            />
                          </TableCell>
                          <TableCell>{challan.challanId}</TableCell>
                          <TableCell>
                            <Chip
                              label={challan.secondEnrollChallan ? "2nd Enrollment" : "Admission"}
                              size="small"
                              color={challan.secondEnrollChallan ? "secondary" : "primary"}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: "600" }}>Rs. {challan.amount}</TableCell>
                          <TableCell>
                            <Chip
                              icon={challan.paid ? <VerifiedIcon /> : <CancelIcon />}
                              label={challan.paid ? "Paid" : "Unpaid"}
                              color={challan.paid ? "success" : "default"}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{new Date(challan.createdAt).toLocaleDateString()}</TableCell>
                          <TableCell align="center">
                            <Box display="flex" justifyContent="center" gap={1}>
                              <Tooltip title="Copy Challan Link">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleCopyChallanLink(challan)}
                                >
                                  <CopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Re-generate PDF">
                                <IconButton
                                  size="small"
                                  color="info"
                                  onClick={() => handleOpenRegenerateChallan(challan)}
                                >
                                  <AutorenewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Challan">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleOpenDeleteChallan(challan)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid #e0e0e0", px: 3, py: 1.5 }}>
          <Button onClick={() => setChallansModalOpen(false)} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Challan Confirmation Dialog */}
      <Dialog
        open={deleteChallanConfirmOpen}
        onClose={() => !deletingChallan && setDeleteChallanConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningIcon color="error" />
          Delete Challan
        </DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to delete challan <strong>{selectedChallanToDelete?.challanId}</strong>?
            </Typography>
            <Typography variant="body2" color="error" sx={{ mt: 1, mb: 2 }}>
              This will permanently delete the database record and also delete the challan PDF file from S3 and local storage.
            </Typography>
            <TextField
              fullWidth
              label="Reason for deletion"
              placeholder="e.g., Error in fee structure, duplicate challan"
              value={deleteChallanReason}
              onChange={(e) => setDeleteChallanReason(e.target.value)}
              multiline
              rows={2}
              required
              disabled={deletingChallan}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteChallanConfirmOpen(false)}
            disabled={deletingChallan}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteChallanConfirm}
            color="error"
            variant="contained"
            disabled={!deleteChallanReason.trim() || deletingChallan}
            startIcon={deletingChallan ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deletingChallan ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Challans Confirmation Dialog */}
      <Dialog
        open={bulkDeleteChallanConfirmOpen}
        onClose={() => !deletingChallan && setBulkDeleteChallanConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningIcon color="error" />
          Bulk Delete Challans
        </DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to delete <strong>{selectedChallanIds.length}</strong> selected challans?
            </Typography>
            <Typography variant="body2" color="error" sx={{ mt: 1, mb: 2 }}>
              This will permanently delete these database records and their associated PDF files from S3 and local storage.
            </Typography>
            <TextField
              fullWidth
              label="Reason for deletion"
              placeholder="Explain why these challans are being deleted..."
              value={bulkDeleteChallanReason}
              onChange={(e) => setBulkDeleteChallanReason(e.target.value)}
              multiline
              rows={2}
              required
              disabled={deletingChallan}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setBulkDeleteChallanConfirmOpen(false)}
            disabled={deletingChallan}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkDeleteChallansConfirm}
            color="error"
            variant="contained"
            disabled={!bulkDeleteChallanReason.trim() || deletingChallan}
            startIcon={deletingChallan ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deletingChallan ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Regenerate Challan Confirmation Dialog */}
      <Dialog
        open={regenerateConfirmOpen}
        onClose={() => !regenerating && setRegenerateConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AutorenewIcon color="info" />
          Regenerate Challan PDF
        </DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to regenerate the PDF file for challan <strong>{selectedChallanToRegenerate?.challanId}</strong>?
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 2 }}>
              This will re-create the PDF document using the existing challan ID and student registration details, then upload it to S3/local storage.
            </Typography>
            <TextField
              fullWidth
              label="Reason for regeneration"
              placeholder="e.g., File missing on S3, user details updated"
              value={regenerateReason}
              onChange={(e) => setRegenerateReason(e.target.value)}
              multiline
              rows={2}
              required
              disabled={regenerating}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRegenerateConfirmOpen(false)}
            disabled={regenerating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRegenerateChallanConfirm}
            color="primary"
            variant="contained"
            disabled={!regenerateReason.trim() || regenerating}
            startIcon={regenerating ? <CircularProgress size={20} /> : <AutorenewIcon />}
          >
            {regenerating ? "Regenerating..." : "Regenerate"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Column Selection for CSV Download Dialog */}
      <Dialog
        open={downloadModalOpen}
        onClose={() => setDownloadModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <ViewColumnIcon color="primary" />
            <Typography variant="h6">Select Columns to Export</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Choose the columns you want to include in the downloaded CSV file. Active filters will be respected.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {/* Campaign Preset / Quick Selection */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 2.5,
              backgroundColor: "rgba(25, 118, 210, 0.04)",
              border: "1px dashed",
              borderColor: "primary.main",
              borderRadius: 1.5,
            }}
          >
            <Box
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              gap={1.5}
            >
              <Box>
                <Typography variant="subtitle2" color="primary" fontWeight="bold">
                  Platform Mailer Template Preset
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Quickly select the required campaign columns: <code>username</code>, <code>duedate</code>, <code>challanId</code>, <code>paddedChallanId</code>, <code>psid</code>
                </Typography>
              </Box>
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={() => {
                  setSelectedColumns(["username", "duedate", "challanId", "paddedChallanId", "psid"]);
                }}
              >
                Use Campaign Preset
              </Button>
            </Box>
          </Paper>

          {/* Export Settings: Due Date & PSID Prefix */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
              mb: 2.5,
              p: 2,
              backgroundColor: "background.paper",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <TextField
              label="Challan Due Date (duedate column)"
              type="date"
              size="small"
              value={exportDueDate}
              onChange={(e) => setExportDueDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Value populated in the duedate column"
              fullWidth
            />
            <TextField
              label="PSID Prefix (psid column)"
              size="small"
              value={exportPsidPrefix}
              onChange={(e) => setExportPsidPrefix(e.target.value)}
              placeholder="e.g. 267200309"
              helperText="Prefixed before each Challan ID (Default: 267200309)"
              fullWidth
            />
          </Box>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Selected: {selectedColumns.length} of {AVAILABLE_EXPORT_COLUMNS.length} columns
            </Typography>
            <Box display="flex" gap={1}>
              <Button
                size="small"
                variant="outlined"
                onClick={handleSelectAllColumns}
                disabled={selectedColumns.length === AVAILABLE_EXPORT_COLUMNS.length}
              >
                Select All
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                onClick={handleDeselectAllColumns}
                disabled={selectedColumns.length === 0}
              >
                Clear All
              </Button>
            </Box>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
              },
              gap: 1.5,
            }}
          >
            {AVAILABLE_EXPORT_COLUMNS.map((column) => {
              const isChecked = selectedColumns.includes(column.key);
              return (
                <Paper
                  key={column.key}
                  variant="outlined"
                  sx={{
                    p: 1,
                    display: "flex",
                    alignItems: "center",
                    borderRadius: 1,
                    cursor: "pointer",
                    borderColor: isChecked ? "primary.main" : "divider",
                    backgroundColor: isChecked ? "action.hover" : "background.paper",
                    transition: "all 0.15s ease-in-out",
                    "&:hover": {
                      backgroundColor: "action.selected",
                    },
                  }}
                  onClick={() => handleToggleColumn(column.key)}
                >
                  <Checkbox
                    checked={isChecked}
                    onChange={() => handleToggleColumn(column.key)}
                    size="small"
                    sx={{ p: 0.5, mr: 1 }}
                  />
                  <Typography variant="body2" sx={{ userSelect: "none" }}>
                    {column.label}
                  </Typography>
                </Paper>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setDownloadModalOpen(false)}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={downloadUsers}
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            disabled={selectedColumns.length === 0 || loading}
          >
            {loading ? "Exporting..." : `Download CSV (${selectedColumns.length} Cols)`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Users;
